import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { Button, DatePicker, Input, ToggleButton } from "@/shared/ui";
import { type Locale } from "@/shared/utils";
import type { GovernmentAwardRecord } from "../../../types/personalSheet";
import { editGovernmentAwardEntrySchema } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  records: GovernmentAwardRecord[];
  initialHasNoGovernmentAwards?: boolean;
  recordsEditable?: boolean;
  draftAddedRecords?: GovernmentAwardRecord[];
}

interface GovernmentAwardEntry {
  id: number | null;
  award_name: string;
  issued_by: string;
  issued_date: string;
}

function makeEmptyEntry(): GovernmentAwardEntry {
  return { id: null, award_name: "", issued_by: "", issued_date: "" };
}

function fromRecord(record: GovernmentAwardRecord): GovernmentAwardEntry {
  return { id: record.id, award_name: record.award_name, issued_by: record.issued_by, issued_date: record.issued_date ?? "" };
}

function isFilledEntry(entry: GovernmentAwardEntry): boolean {
  return entry.award_name.trim() !== "";
}

function toApiEntry(entry: GovernmentAwardEntry): Record<string, unknown> {
  const api: Record<string, unknown> = { award_name: entry.award_name, issued_by: entry.issued_by, issued_date: entry.issued_date || null };
  if (entry.id != null) api.id = entry.id;
  return api;
}

function entriesSignature(entries: GovernmentAwardEntry[]): string {
  return JSON.stringify(entries.filter(isFilledEntry).map(toApiEntry));
}

const EditGovernmentAwardsSection = forwardRef<SectionFormHandle, Props>(function EditGovernmentAwardsSection(
  { records, initialHasNoGovernmentAwards, recordsEditable = false, draftAddedRecords },
  ref,
) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";

  const [hasNoGovernmentAwards, setHasNoGovernmentAwards] = useState(initialHasNoGovernmentAwards ?? false);
  const [newEntries, setNewEntries] = useState<GovernmentAwardEntry[]>(() =>
    recordsEditable ? records.map(fromRecord) : (draftAddedRecords ?? []).map(fromRecord),
  );
  const initialSignatureRef = useRef<string>(entriesSignature(recordsEditable ? records.map(fromRecord) : []));
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});

  function handleAddEntry() {
    setNewEntries((prev) => [...prev, makeEmptyEntry()]);
  }

  function handleRemoveEntry(index: number) {
    setNewEntries((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function updateEntry(index: number, field: keyof GovernmentAwardEntry, value: string) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  function getFlagChanges() {
    if (hasNoGovernmentAwards !== (initialHasNoGovernmentAwards ?? false)) {
      return { has_no_government_awards: hasNoGovernmentAwards };
    }
    return null;
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        if (hasNoGovernmentAwards) return [] as unknown as Record<string, unknown>;
        if (recordsEditable) {
          if (entriesSignature(newEntries) === initialSignatureRef.current) return null;
          return newEntries.filter(isFilledEntry).map(toApiEntry) as unknown as Record<string, unknown>;
        }
        if (!newEntries.some(isFilledEntry)) return null;
        const allRecords = [...records.map(fromRecord), ...newEntries.filter(isFilledEntry)].map(toApiEntry);
        return allRecords as unknown as Record<string, unknown>;
      },
      getFiles() {
        return null;
      },
      async validate() {
        if (hasNoGovernmentAwards) {
          setValidationErrors({});
          return true;
        }
        const errors: Record<number, Record<string, string>> = {};
        let allValid = true;
        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;
          const result = editGovernmentAwardEntrySchema.safeParse({
            award_name: entry.award_name,
            issued_by: entry.issued_by,
            issued_date: entry.issued_date,
          });
          if (!result.success) {
            allValid = false;
            const entryErrors: Record<string, string> = {};
            for (const issue of result.error.issues) {
              entryErrors[issue.path[0] as string] = issue.message;
            }
            errors[i] = entryErrors;
          }
        }
        setValidationErrors(errors);
        return allValid;
      },
      isDirty() {
        const flagChanged = hasNoGovernmentAwards !== (initialHasNoGovernmentAwards ?? false);
        if (recordsEditable) return flagChanged || entriesSignature(newEntries) !== initialSignatureRef.current;
        return flagChanged || newEntries.some(isFilledEntry);
      },
      getFlagChanges,
    };
  });

  const showReadOnly = !recordsEditable;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <span className="text-label-sm content-base-secondary">{t("personalSheet.hasNoGovernmentAwards")}</span>
        <ToggleButton checked={hasNoGovernmentAwards} onChange={setHasNoGovernmentAwards} />
      </div>

      {hasNoGovernmentAwards ? (
        <p className="text-body-regular-sm content-base-secondary p-3 surface-secondary-fill radius-sm">
          {t("personalSheet.noGovernmentAwardsNote")}
        </p>
      ) : (
        <>
          {showReadOnly &&
            records.map((record, index) => (
              <div key={record.id} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
                <p className="text-body-bold-md content-base-primary">{t("personalSheet.governmentAwardRecord", { number: index + 1 })}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label={t("personalSheet.fields.awardName")} placeholder="" value={record.award_name} disabled />
                  <Input label={t("personalSheet.fields.issuedBy")} placeholder="" value={record.issued_by} disabled />
                </div>
                <DatePicker
                  label={t("personalSheet.fields.issuedDate")}
                  mode="single"
                  locale={locale}
                  value={record.issued_date ? new Date(record.issued_date) : null}
                  onChange={() => {}}
                  disabled
                />
              </div>
            ))}

          {newEntries.map((entry, index) => (
            <div key={index} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.governmentAwardRecord", { number: (showReadOnly ? records.length : 0) + index + 1 })}
                </p>
                <Button variant="danger" size="sm" onClick={() => handleRemoveEntry(index)}>
                  <Trash size={16} color="currentColor" />
                  {t("personalSheet.removeRecord")}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t("personalSheet.fields.awardName")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.awardName")}
                  value={entry.award_name}
                  onChange={(e) => updateEntry(index, "award_name", e.target.value)}
                  error={validationErrors[index]?.award_name ? t(validationErrors[index].award_name) : undefined}
                />
                <Input
                  label={t("personalSheet.fields.issuedBy")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.issuedBy")}
                  value={entry.issued_by}
                  onChange={(e) => updateEntry(index, "issued_by", e.target.value)}
                  error={validationErrors[index]?.issued_by ? t(validationErrors[index].issued_by) : undefined}
                />
              </div>
              <DatePicker
                label={t("personalSheet.fields.issuedDate")}
                hasRequiredLabel
                mode="single"
                locale={locale}
                value={entry.issued_date ? new Date(entry.issued_date) : null}
                onChange={(d) => updateEntry(index, "issued_date", d ? (d as Date).toISOString().split("T")[0] : "")}
                clearable
                error={validationErrors[index]?.issued_date ? t(validationErrors[index].issued_date) : undefined}
              />
            </div>
          ))}

          <Button variant="tertiary" size="lg" onClick={handleAddEntry} className="w-full text-center">
            <Add size={16} color="currentColor" />
            {t("personalSheet.addGovernmentAward")}
          </Button>
        </>
      )}
    </div>
  );
});

export default EditGovernmentAwardsSection;
